import { defineMessages } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';

import { Attribution } from '@confluence/error-boundary';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';

export const i18n = defineMessages({
	unknownError: {
		id: 'live-pages-features.converttolivepage.modal.error.unknown',
		defaultMessage: 'An error occurred. Please try again.',
		description: 'Dialog content body when error happens in convert to live pages dialog',
	},
	serverException: {
		id: 'live-pages-features.converttolivepage.modal.error.serverException',
		defaultMessage: 'An unexpected error occurred. Please try again later.',
		description:
			'Displayed when an unexpected server-side error prevents the page from being converted to live edit mode.',
	},
	pageNotFound: {
		id: 'live-pages-features.converttolivepage.modal.error.pageNotFound',
		defaultMessage: 'The page you‘re trying to edit doesn‘t exist.',
		description:
			'Shown when the user attempts to convert a page to live edit mode, but the specified page ID does not exist or cannot be found.',
	},
	contentServiceException: {
		id: 'live-pages-features.converttolivepage.modal.error.contentServiceException',
		defaultMessage: 'The requested content is not available.',
		description:
			'Appears when the content requested for conversion to live edit mode cannot be retrieved, possibly due to access restrictions or deletion.',
	},
	pageAlreadyLive: {
		id: 'live-pages-features.converttolivepage.modal.error.pageAlreadyLive',
		defaultMessage: 'This page has already been converted to live edit mode.',
		description:
			'Informs the user that the page they are attempting to convert is already in live edit mode and does not need conversion.',
	},
	legacyPageConversionError: {
		id: 'live-pages-features.converttolivepage.modal.error.legacyPageConversionError',
		defaultMessage: 'This page cannot be converted to live edit due to its legacy format.',
		description:
			'Displayed when a page uses a legacy format that is not compatible with the live edit feature.',
	},
	noEditPermissions: {
		id: 'live-pages-features.converttolivepage.modal.error.noEditPermissions',
		defaultMessage: 'You don‘t have the necessary permissions to convert this to a live edit page.',
		description:
			'Displayed when the user lacks the edit permissions required to convert a page to live edit mode.',
	},
	draftTitleConflict: {
		id: 'live-pages-features.converttolivepage.draft-conversion-title-conflict.description',
		defaultMessage:
			'There’s already a page named “{pageTitle}” in this space. Give this page a unique title.',
		description:
			'error description shown to user when their page has the same title as another page, preventing them from converting it',
	},
});

export const getI18nStringForError = (errorMessage: string | null) => {
	switch (errorMessage) {
		case 'Some server exception':
			return i18n.serverException;
		case 'Page not found with provided id':
			return i18n.pageNotFound;
		case 'Content service exception':
			return i18n.contentServiceException;
		case 'Page subtype is already set to live':
			return i18n.pageAlreadyLive;
		case 'Legacy page cannot be converted to live edit':
			return i18n.legacyPageConversionError;
		case 'Page cannot be converted without edit permissions':
			return i18n.noEditPermissions;
		case 'Title already exists':
			return i18n.draftTitleConflict;
		default:
			return i18n.unknownError;
	}
};

export const submitErrorToMonitoringSystem = (error: ApolloError | Error, contentId: string) => {
	if (!error.message) return;
	setContentErrorAttributes(error, {
		objectId: contentId,
	});
	getMonitoringClient().submitError(error, {
		attribution: Attribution.PAGE_MODES,
	});
};
